import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth';

export const ROUTE_NAMES = {
  dashboard: 'dashboard',
  signIn: 'sign-in',
} as const;

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: ROUTE_NAMES.dashboard,
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/sign-in',
    name: ROUTE_NAMES.signIn,
    component: () => import('@/pages/SignInPage.vue'),
  },
  // Everything else is a mistyped URL rather than a route we have retired,
  // so it goes to the dashboard instead of a 404 page nobody would benefit
  // from. The dashboard is the only destination this application has.
  { path: '/:pathMatch(.*)*', redirect: { name: ROUTE_NAMES.dashboard } },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * Resolves the session once, then gates the dashboard.
 *
 * There are two ways to be turned away and they are deliberately not merged.
 * A signed-out visitor gets a prompt. Somebody signed in to GitHub who is not
 * in the `ways2well` org gets an explanation - and reaches it by one of two
 * routes: the OAuth callback redirects to `/?error=not_org_member` without
 * setting a cookie, and an existing session whose hourly membership re-check
 * fails starts returning 403 mid-use. The first is "you cannot come in", the
 * second is "you have been removed while you were working", and showing the
 * same words for both would be confusing in opposite directions.
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Carried from the OAuth callback. Read here rather than in the page so a
  // deep link lands on the right screen rather than flashing the dashboard.
  const rejected = to.query.error === 'not_org_member';

  if (!auth.isResolved) await auth.resolve();

  if (rejected && to.name !== ROUTE_NAMES.signIn) {
    return { name: ROUTE_NAMES.signIn, query: { error: 'not_org_member' } };
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return {
      name: ROUTE_NAMES.signIn,
      // Only meaningful for a real path; sending "/" back as a return target
      // would add a query parameter that says nothing.
      query: to.fullPath === '/' ? {} : { redirect: to.fullPath },
    };
  }

  // A resolved session has no business sitting on the sign-in screen.
  if (to.name === ROUTE_NAMES.signIn && auth.isAuthenticated && !rejected) {
    return { name: ROUTE_NAMES.dashboard };
  }

  return true;
});

/**
 * Registered here rather than inside the client, which must not know about
 * routing. A 401 arriving later - an expired session, or one revoked because
 * membership lapsed - sends the user to the sign-in screen once.
 */
export function installUnauthorizedRedirect(): void {
  api.setUnauthorizedHandler(() => {
    const auth = useAuthStore();
    auth.markSignedOut();

    if (router.currentRoute.value.name !== ROUTE_NAMES.signIn) {
      void router.replace({ name: ROUTE_NAMES.signIn });
    }
  });
}
